/**
 * Column-menu filters for a server-paged grid. The grid fetches one page at a time, so a filter
 * applied in the browser only sees that page. These helpers turn the grid's filter model into the
 * same `{ field, operator, value }` filters Search sends (the field is the column's output name,
 * which the API resolves), and restrict each column to the operators the server can apply.
 */
import {
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  type GridFilterModel,
  type GridFilterOperator,
} from "@mui/x-data-grid";
import type { FilterOperator } from "../../types";

/** A filter as the view query sends it. */
export interface ColumnFilter {
  field: string;
  operator: FilterOperator;
  value?: unknown;
}

const SERVER_OPERATOR: Record<string, FilterOperator> = {
  contains: "contains",
  equals: "eq",
  startsWith: "starts_with",
  endsWith: "ends_with",
  "=": "eq",
  "!=": "ne",
  ">": "gt",
  ">=": "gte",
  "<": "lt",
  "<=": "lte",
  // Boolean "is". Date comparisons are not offered: the API binds a filter value as sent, so a
  // date or timestamp would not compare reliably until it coerces temporal values.
  is: "eq",
  isEmpty: "is_null",
  isNotEmpty: "is_not_null",
};

const NUMERIC_OPERATORS = new Set(["=", "!=", ">", ">=", "<", "<="]);

/** The grid operators for a column type that have a server equivalent. */
export function serverFilterOperators(type?: string): GridFilterOperator[] {
  const all: GridFilterOperator[] =
    type === "number"
      ? getGridNumericOperators()
      : type === "boolean"
        ? getGridBooleanOperators()
        : type === "date" || type === "dateTime"
          ? (getGridDateOperators(type === "dateTime") as GridFilterOperator[])
          : getGridStringOperators();
  const temporal = type === "date" || type === "dateTime";
  // Text is the default type. Its grid "is empty" also matches an empty string, which the
  // server's NULL test does not, so emptiness is offered for other types only.
  const text = !type || type === "string";
  return all.filter(
    (operator) =>
      operator.value in SERVER_OPERATOR &&
      (!temporal ||
        operator.value === "isEmpty" ||
        operator.value === "isNotEmpty") &&
      !(
        text &&
        (operator.value === "isEmpty" || operator.value === "isNotEmpty")
      ),
  );
}

/** The server filters for a grid filter model; incomplete or unsupported items are skipped. */
export function toServerFilters(model: GridFilterModel): ColumnFilter[] {
  const filters: ColumnFilter[] = [];
  for (const item of model.items) {
    const operator = SERVER_OPERATOR[item.operator];
    if (!item.field || !operator) continue;
    if (operator === "is_null" || operator === "is_not_null") {
      // The API requires the value key even for an emptiness test.
      filters.push({ field: item.field, operator, value: null });
      continue;
    }
    const raw = item.value;
    if (raw === undefined || raw === null || raw === "") continue;
    let value: unknown = raw;
    if (NUMERIC_OPERATORS.has(item.operator)) {
      const numeric = Number(raw);
      if (!Number.isFinite(numeric)) continue;
      value = numeric;
    } else if (item.operator === "is") {
      // Only the boolean "is": anything but true/false (a date, say) cannot be applied.
      if (raw !== "true" && raw !== "false" && typeof raw !== "boolean")
        continue;
      value = raw === true || raw === "true";
    } else if (typeof raw === "object") {
      continue;
    }
    filters.push({ field: item.field, operator, value });
  }
  return filters;
}

/**
 * A predicate for the columns the server can filter. A link (REFERENCE) column shows a value the
 * server builds from one or more source columns (a person's first and last name, say), which a
 * filter on a single source column would not match, so it is not filterable.
 */
export function serverFilterableColumns(
  columns: Array<{ field: string; editMode?: string }>,
): (field: string) => boolean {
  const blocked = new Set(
    columns
      .filter((column) => column.editMode === "REFERENCE")
      .map((column) => column.field),
  );
  return (field) => !blocked.has(field);
}
